import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import ModalVideo from '@fec/frontend-aron/modules/media/ModalVideo/ModalVideo.jsx';
import { MEDIA_CLICKED, MEDIA_ENDED } from '@fec/assets/js/utils/event.js';
import { ResponsiveHelper } from '@fec/frontend/foundation/client/responsive-helper';
import { useTranslation } from 'react-i18next';

import './VideoGalleryPlayer.scss';

const VideoGalleryPlayer = ({
  assets = [],
  activeVideoGalleryItem,
  setActiveVideoGalleryItem,
}) => {
  const { t } = useTranslation();
  const [isInitialized, setIsInitialized] = useState(false);
  const playerRef = useRef(null);
  const galleryAssetFrontendIds = assets.map((asset) => asset.frontendId);

  // handle clicks on video gallery items
  useEffect(() => {
    const handleMediaClicked = (event) => {
      if (galleryAssetFrontendIds.includes(event.detail.frontendId)) {
        if (!isInitialized) {
          let timeout = 0;
          if (ResponsiveHelper.isMobile()) {
            // on mobile, where a gallery item might be partially visible, they are
            // always scrolled into view.
            //
            // but chrome can't handle two simultaneous scrollIntoView calls with the
            // behaviour 'smooth' (it would cancel the first call) so we need to wait
            // a bit before calling this second one.
            //
            // btw, this is not an issue in other browsers although chrome
            // follows the spec: https://drafts.csswg.org/cssom-view/#scrolling
            timeout = 500;
          }

          window.setTimeout(() => {
            playerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }, timeout);
        }
        setIsInitialized(true);
        setActiveVideoGalleryItem({
          urn: event.detail.urn,
          frontendId: event.detail.frontendId,
          title: event.detail.title,
        });
      }
    };

    window.addEventListener(MEDIA_CLICKED, handleMediaClicked);
    return () => window.removeEventListener(MEDIA_CLICKED, handleMediaClicked);
  }, [galleryAssetFrontendIds]);

  // handle media ended events (-> auto-continue feature)
  useEffect(() => {
    const handleMediaEnded = (event) => {
      if (event.detail.frontendId === activeVideoGalleryItem.frontendId) {
        const activeAssetIndex = assets.findIndex(
          (asset) => asset.frontendId === activeVideoGalleryItem.frontendId,
        );

        if (activeAssetIndex !== -1) {
          const nextAsset = assets.at(activeAssetIndex + 1);
          if (nextAsset) {
            setActiveVideoGalleryItem({
              urn: nextAsset.urn,
              frontendId: nextAsset.frontendId,
              title: nextAsset.title,
            });
          }
        }
      }
    };
    window.addEventListener(MEDIA_ENDED, handleMediaEnded);
    return () => {
      window.removeEventListener(MEDIA_ENDED, handleMediaEnded);
    };
  }, [assets, activeVideoGalleryItem]);

  // when the last element in the player is focused, the focus goes back to the active video in the gallery list below the player
  const handleLastElementFocused = () => {
    setActiveVideoGalleryItem({
      ...activeVideoGalleryItem,
      isFocused: !activeVideoGalleryItem.isFocused,
    });
  };

  return (
    <div
      className={classNames('video-gallery-player', {
        'video-gallery-player--active': isInitialized,
      })}
      ref={playerRef}>
      <div className="video-gallery-player__player">
        {isInitialized && (
          <>
            <ModalVideo
              urn={activeVideoGalleryItem.urn}
              frontendId={activeVideoGalleryItem.frontendId}
              playerA11yLabel={t('videoGalleryPlayer.a11yPlayerLabel', {
                title: activeVideoGalleryItem.title,
              })}
            />
            {/* since we don't close the video and it's before the video gallery items in the DOM, this little shortcut teleports the focus to the "teaser" for the currently active video */}
            <button
              className="h-offscreen h-offscreen-focusable"
              onFocus={handleLastElementFocused}
              onClick={handleLastElementFocused}>
              {t('videoGalleryPlayer.a11yButtonLabel')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

VideoGalleryPlayer.propTypes = {
  assets: PropTypes.array,
  activeVideoGalleryItem: PropTypes.object,
  setActiveVideoGalleryItem: PropTypes.func,
};

export default VideoGalleryPlayer;
